import { CheckIcon, DocumentIcon, XCircleIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useMemo } from 'react';
import type { Accept } from 'react-dropzone';
import { PrimaryButton } from '../../../components/buttons';
import { Spinner } from '../../../components/spinner';
import { type FileRow, useInvoiceUpload } from '../hooks/use-invoice-upload';
import { FileDropzone } from './file-dropzone';
import { FormatPill } from './format-pill';
import { invoiceFormatLabel } from './invoice-formats';
import { StepHeading } from './step-heading';

const UPLOAD_ERROR_MSG = 'Upload failed. Please retry.';

const FORMAT_MIME_TYPES: Record<string, string> = {
  csv: 'text/csv',
  eml: 'application/octet-stream',
  pdf: 'application/pdf',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  html: 'text/html',
};

function buildAccept(formats: string[]): Accept {
  const grouped: Record<string, string[]> = {};
  for (const raw of formats) {
    const ext = raw.replace(/^\./, '').toLowerCase();
    const mime = FORMAT_MIME_TYPES[ext] ?? 'application/octet-stream';
    if (!grouped[mime]) {
      grouped[mime] = [];
    }

    grouped[mime].push(`.${ext}`);
  }

  return grouped;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }

  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }

  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function FileRowStatus({ status }: { status: FileRow['status'] }) {
  switch (status) {
    case 'pending':
      return null;
    case 'uploading':
      return <Spinner sizeClass="h-4 w-4" />;
    case 'done':
      return <CheckIcon className="h-4 w-4 text-green-600" aria-label="uploaded" />;
    case 'failed':
      return <XCircleIcon className="h-4 w-4 text-red-600" aria-label="failed" />;
  }
}

export type FileUploadProps = {
  vendorName: string;
  acceptedFormats: string[];
  disabled?: boolean;
  onUploadFile: (file: File) => Promise<void>;
  onProcess: () => Promise<void>;
};

export function FileUpload({
  vendorName,
  acceptedFormats,
  disabled = false,
  onUploadFile,
  onProcess,
}: FileUploadProps) {
  const accept = useMemo(() => buildAccept(acceptedFormats), [acceptedFormats]);
  const acceptsCopy = useMemo(
    () => acceptedFormats.map(invoiceFormatLabel).join(', '),
    [acceptedFormats]
  );

  const {
    rows,
    dropError,
    processing,
    processError,
    onDrop,
    removeRow,
    runProcess,
    handleProcessClick,
    handleRetry,
  } = useInvoiceUpload({ vendorName, acceptsCopy, onUploadFile, onProcess });

  const hasPdfFiles = useMemo(() => rows.some((r) => r.file.type === 'application/pdf'), [rows]);
  const processButtonLabel = processing
    ? 'Processing\u2026'
    : `Process ${rows.length} ${rows.length === 1 ? 'invoice' : 'invoices'}`;

  return (
    <div className="flex flex-col gap-4 min-h-0">
      <StepHeading>Add {vendorName} invoice files</StepHeading>

      {processError && (
        <div className="rounded-sm border border-red-200 bg-red-50 p-3 text-sm text-red-800 flex items-center justify-between gap-2">
          <span>Couldn't start processing: {processError}</span>
          <button
            type="button"
            onClick={runProcess}
            disabled={disabled || processing}
            className="text-sm font-medium text-red-900 hover:underline shrink-0 cursor-pointer disabled:cursor-not-allowed"
          >
            Try again
          </button>
        </div>
      )}

      {hasPdfFiles && (
        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md flex items-start gap-2">
          <ExclamationTriangleIcon
            className="h-5 w-5 text-yellow-400 shrink-0 mt-0.5"
            aria-hidden="true"
          />
          <div className="text-sm text-yellow-800">
            <p className="font-medium">Extracting data from PDF files</p>
            <p className="mt-1">
              PDF extraction is imperfect: please carefully check the review step for missing
              products and numerical errors.
            </p>
          </div>
        </div>
      )}

      <FileDropzone
        accept={accept}
        label="Select invoice files"
        onDrop={onDrop}
        disabled={disabled || processing}
      >
        <div className="mt-3 flex flex-wrap justify-center gap-1">
          {acceptedFormats.map((f) => (
            <FormatPill key={f} label={invoiceFormatLabel(f)} />
          ))}
        </div>
      </FileDropzone>

      {dropError && <p className="text-sm text-red-600">{dropError}</p>}

      {rows.length > 0 && (
        <ul className="flex flex-col gap-1">
          {rows.map((r) => (
            <li
              key={r.id}
              className="flex items-center gap-2 border border-gray-200 rounded px-3 py-2 text-sm text-gray-700"
            >
              <DocumentIcon className="h-4 w-4 text-gray-400 shrink-0" aria-hidden="true" />
              <span className="truncate flex-1">{r.file.name}</span>
              {r.status === 'failed' && (
                <span className="text-xs text-red-600">{UPLOAD_ERROR_MSG}</span>
              )}
              <span className="text-gray-400 text-xs shrink-0">{formatSize(r.file.size)}</span>
              <span className="shrink-0 inline-flex items-center">
                <FileRowStatus status={r.status} />
              </span>
              {r.status === 'failed' && (
                <button
                  type="button"
                  onClick={() => handleRetry(r.id)}
                  disabled={disabled || processing}
                  className="text-xs text-primary-600 hover:text-primary-800 shrink-0 cursor-pointer disabled:cursor-not-allowed"
                  aria-label={`Retry ${r.file.name}`}
                >
                  Retry
                </button>
              )}
              <button
                type="button"
                onClick={() => removeRow(r.id)}
                disabled={disabled || r.status === 'uploading' || processing}
                className="text-gray-400 hover:text-red-500 shrink-0 cursor-pointer disabled:cursor-not-allowed"
                aria-label={`Remove ${r.file.name}`}
              >
                <XMarkIcon className="h-4 w-4" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {rows.length > 0 && (
        <div className="flex justify-end">
          <PrimaryButton
            type="button"
            onClick={handleProcessClick}
            disabled={disabled || processing || rows.some((r) => r.status === 'uploading')}
          >
            {processButtonLabel}
          </PrimaryButton>
        </div>
      )}
    </div>
  );
}
